import React, { useState, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'

interface File {
  id: string
  name: string
  size: number
  type: string
  uploadedAt: string
  url?: string
}

const FileUploadPage: React.FC = () => {
  const { user } = useAuth()
  const [files, setFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
    if (!selectedFiles) return

    const newFiles: File[] = Array.from(selectedFiles).map(file => ({
      id: Date.now().toString() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      uploadedAt: new Date().toISOString(),
      url: URL.createObjectURL(file)
    }))

    setFiles([...files, ...newFiles])
    toast.success(`${newFiles.length} file selezionati`)
  }

  const uploadFiles = async () => {
    if (files.length === 0) {
      toast.error('Seleziona almeno un file da caricare')
      return
    }

    setIsUploading(true)
    
    // Simulate upload
    setTimeout(() => {
      toast.success('File caricati con successo!')
      setIsUploading(false)
    }, 2000)
  }

  const deleteFile = (id: string) => {
    setFiles(files.filter(file => file.id !== id))
    toast.success('File eliminato')
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Upload File
          </h3>
          
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4">
              Carica i tuoi file per condividerli in modo sicuro
            </p>
          </div>

          {/* Upload Area */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-6">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Seleziona File
            </button>
            <p className="mt-2 text-sm text-gray-500">
              Trascina i file qui o clicca per selezionarli
            </p>
          </div>

          {/* Files List */}
          {files.length > 0 && (
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-3">File Selezionati</h4>
              <div className="space-y-2">
                {files.map(file => (
                  <div key={file.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="text-blue-600 text-sm font-medium">
                          {file.type.split('/')[0].charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(file.size)} • {file.type}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => deleteFile(file.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Elimina
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          {files.length > 0 && (
            <div className="mb-6">
              <button
                onClick={uploadFiles}
                disabled={isUploading}
                className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                {isUploading ? 'Caricamento in corso...' : `Carica ${files.length} File`}
              </button>
            </div>
          )}

          {/* Uploaded Files */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">File Caricati</h4>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-500 text-center">
                I file caricati appariranno qui
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FileUploadPage 